/**
 * Shared Datadog RUM view naming utilities.
 *
 * Kept in one place so features like route viewName tracking and RUM actions
 * can use the same URL normalization + module alias mapping.
 */

const RUM_WORKFLOW_WRAPPERS = ['forms', 'workflow-management', 'user-management', 'integrations', 'users', 'pages'];

const RUM_MODULE_ALIASES: { [key: string]: string } = {
	generalledger: 'general_ledger',
	'miscellaneous-billing': 'billing_ar',
	'projects-grants-mb': 'projects_grants',
	'project-grants': 'projects_grants',
	'inventory-and-work-orders': 'inventory_and_work_orders',
	'capital-assets': 'capital_assets',
	'capital-lease': 'lease_accounting',
	licenses: 'licensing',
	hris: 'hr_payroll',
	'code-enforcement': 'code_enforcement',
	announcements: 'announcements',
	training: 'training',
	segmentation: 'segmentation',
	'policies-procedures': 'policies_procedures',
};

const isUuidLike = (value: string): boolean => /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value);

const isNumericId = (value: string): boolean => /^\d+$/.test(value);

const normalizePathToSegments = (url: string): string[] => {
	if (!url || url === '/' || url === '#/' || url === '#') return [];

	let path = url;
	const hashIndex = path.indexOf('#');
	if (hashIndex >= 0) path = path.slice(hashIndex + 1);
	path = path.replace(/^\//, '').split('?')[0];

	const segments = path.split('/').filter(Boolean);
	if (!segments.length) return [];

	if (segments.length && RUM_WORKFLOW_WRAPPERS.includes(segments[0])) {
		return segments.slice(1);
	}

	return segments;
};

export function getRumViewName(url: string): string {
	const segments = normalizePathToSegments(url);
	if (!segments.length) return 'dashboard';

	const normalizeSegment = (segment: string, index: number): string => {
		// IDs become "detail" so detail screens aggregate together
		if (isUuidLike(segment) || isNumericId(segment)) {
			return 'detail';
		}

		let s = segment.toLowerCase();

		// Apply module aliases on the first meaningful segment
		if (index === 0 && RUM_MODULE_ALIASES[s]) {
			return RUM_MODULE_ALIASES[s];
		}

		s = s.replace(/-/g, '_');

		// Normalize some long suffixes without losing meaning
		if (s.endsWith('_configuration')) {
			return s.replace(/_configuration$/, '_config');
		}

		return s;
	};

	const normalized = segments.map(normalizeSegment);
	return normalized.join('_') || 'dashboard';
}

export function getRumModuleFromUrl(url: string): string {
	const segments = normalizePathToSegments(url);
	if (!segments.length) return 'dashboard';

	const root = segments[0].toLowerCase();
	const normalizedRoot = RUM_MODULE_ALIASES[root] || root.replace(/-/g, '_');
	return normalizedRoot || 'unknown';
}

export function getRumModuleFromViewName(viewName: string): string {
	if (!viewName) return 'unknown';
	if (viewName === 'dashboard') return 'dashboard';

	// Prefer matching the module-root prefix used in viewName:
	// - reporting_issues_queue => reporting
	// - general_ledger_journal_form => general_ledger
	const knownModulePrefixes = [
		'general_ledger',
		'billing_ar',
		'projects_grants',
		'inventory_and_work_orders',
		'capital_assets',
		'lease_accounting',
		'hr_payroll',
		'code_enforcement',
		'policies_procedures',
		'capital_assets',
		'workflow_management',
		'payments',
		'purchasing',
		'budget',
		'treasury',
		'work_orders',
		'cashiering',
		'licensing',
		'announcements',
		'training',
		'segmentation',
		'special_assessment',
		'implementation',
		'reporting',
	];

	const sorted = [...new Set(knownModulePrefixes)].sort((a, b) => b.length - a.length);
	for (const prefix of sorted) {
		if (viewName === prefix || viewName.startsWith(`${prefix}_`)) {
			return prefix;
		}
	}

	// Fallback: first segment before underscore.
	return viewName.split('_')[0] || 'unknown';
}
